/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { jsx, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { AvatarContent } from '@atlaskit/avatar/avatar-content';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

import { AgentVendorIcon, hasAgentVendorIcon } from './AgentVendorIcon.tsx';

export type SuggestionAgentIdentityProps = {
	agent?: Suggestion['createdByAgent'];
	user?: Suggestion['createdByUser'];
	systemGenerated?: Suggestion['systemGenerated'];
};

// `Avatar` always draws a `space.025` margin around its own content (so avatars can sit flush in a
// group). Centering the agent avatar within `stack`'s exact `space.300` box cancels that margin out,
// so the visible hex icon — not `Avatar`'s larger margin box — lines up with `stack`'s edges.
const styles = cssMap({
	stack: {
		// Block-level flex, not inline-flex — `Tooltip` wraps this in a plain `div`, and an
		// inline-level child there would sit in a line box whose inherited `line-height` adds
		// leading above the hex avatar, clipping its top. See `SuggestionCard`'s `privateMetadata`.
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		height: token('space.300'),
		width: token('space.300'),
	},
	// No built-in `Avatar` size is 12px. `small` (24px) scaled by exactly half avoids the blur a
	// less even ratio (e.g. `xxsmall` scaled by 0.75) introduced.
	userBadge: {
		position: 'absolute',
		insetBlockEnd: `calc(-1 * ${token('space.025')})`,
		insetInlineEnd: `calc(-1 * ${token('space.025')})`,
		display: 'flex',
		transform: 'scale(0.5)',
		// `Avatar` always draws a `space.025` margin around its own content, so the anchor for the
		// scale — and the inset above — both offset that margin, keeping the visible avatar (not
		// its margin box) flush with `stack`'s bottom-right corner.
		transformOrigin: `calc(100% - ${token('space.025')}) calc(100% - ${token('space.025')})`,
	},
});

/** Renders the compact Rovo identity used when no agent avatar is available. */
function RovoIdentity(): React.JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<RovoHexIcon label={formatMessage(aiSuggestionsMessages.cardRovoAuthorLabel)} size="small" />
	);
}

function AuthorsTooltip({
	agentName,
	children,
	userName,
}: {
	agentName: string;
	children: React.ReactElement;
	userName?: string;
}): React.JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<Tooltip
			content={
				userName
					? formatMessage(aiSuggestionsMessages.cardAuthorsTooltipLabel, { agentName, userName })
					: agentName
			}
			ignoreTooltipPointerEvents
			position="bottom"
		>
			{children}
		</Tooltip>
	);
}

/** Renders the agent and user identities associated with a suggestion. */
export function SuggestionAgentIdentity({
	agent,
	user,
	systemGenerated,
}: SuggestionAgentIdentityProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const agentName = agent?.name?.trim();
	const userName = user?.name?.trim();
	const validAgent =
		agent?.accountId && agentName
			? {
					name: agentName,
					picture: agent.picture?.trim() || undefined,
				}
			: undefined;
	const validUser =
		!(systemGenerated && isExperimentEnabled('platform_editor_ai_system_generated_suggestions')) &&
		user?.accountId &&
		userName
			? {
					name: userName,
					picture: user.picture?.trim() || undefined,
				}
			: undefined;

	// A vendor icon is a bare SVG, so `AvatarContent` has to draw the hexagon shape for it. A
	// backend-provided agent avatar brings its own via `Avatar`'s `src`/`appearance` props.
	// `borderColor="transparent"` drops the ring `Avatar` draws by default — the user avatar badge
	// now provides the only visual separation.
	const agentSlot = validAgent ? (
		hasAgentVendorIcon(validAgent.name) &&
		!expValEquals('cc_identity_branded_agent_attribution', 'isEnabled', true) ? (
			<Avatar appearance="hexagon" borderColor="transparent" label={validAgent.name} size="small">
				<AvatarContent>
					<AgentVendorIcon agentName={validAgent.name} fallback={<span />} />
				</AvatarContent>
			</Avatar>
		) : (
			<Avatar
				appearance="hexagon"
				borderColor="transparent"
				label={validAgent.name}
				name={validAgent.name}
				size="small"
				src={validAgent.picture}
			/>
		)
	) : (
		<Avatar
			appearance="hexagon"
			borderColor="transparent"
			label={formatMessage(aiSuggestionsMessages.cardRovoAuthorLabel)}
			size="small"
		>
			<AvatarContent>
				<RovoIdentity />
			</AvatarContent>
		</Avatar>
	);

	const authorAvatars = (
		<div css={styles.stack}>
			{agentSlot}
			{validUser && (
				<span css={styles.userBadge} data-testid="suggestion-agent-identity-user-badge">
					<Avatar
						label={validUser.name}
						name={validUser.name}
						size="small"
						src={validUser.picture}
					/>
				</span>
			)}
		</div>
	);

	return (
		<AuthorsTooltip
			agentName={validAgent?.name ?? formatMessage(aiSuggestionsMessages.cardRovoAuthorLabel)}
			userName={validUser?.name}
		>
			{authorAvatars}
		</AuthorsTooltip>
	);
}
